/**
 * Compresses and resizes an image from a Data URL or File.
 * @param {string|File} source - The source image (Data URL or File object)
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width of the output image (default: 1280)
 * @param {number} options.maxHeight - Maximum height of the output image (default: 1280)
 * @param {number} options.quality - JPEG quality from 0 to 1 (default: 0.7)
 * @returns {Promise<string>} - A promise that resolves with the compressed Data URL
 */
const compressImage = (source, options = {}) => {
  const { maxWidth = 1280, maxHeight = 1280, quality = 0.7 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to Data URL with compression
      const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve(compressedDataUrl);
    };

    img.onerror = (err) => {
      reject(new Error("Failed to load image for compression"));
    };

    if (typeof source === "string") {
      img.src = source;
    } else if (source instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        reject(new Error("Failed to read file for compression"));
      };
      reader.readAsDataURL(source);
    } else {
      reject(new Error("Invalid source type for compression"));
    }
  });
};

export default compressImage;
